import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Profile } from "@tilt/pos-sdk/types";

import { getPosClient } from "../../lib/api";

/**
 * PIN management section on the profile page (#149).
 *
 * Three states the cashier sees, derived from ``profile.has_pin``:
 *
 *   no PIN set    → "Set a PIN" button → form (new + confirm)
 *   PIN set       → "Change PIN" + "Clear PIN" buttons → form (current + new + confirm)
 *
 * The same PIN serves the cashier's own fast-switch + the supervisor
 * override on the order-edit gate (#152). Role determines what it
 * unlocks, not a separate column.
 *
 * Validation mirrors the API: 4..6 digits, all numeric. The backend
 * re-validates and is authoritative; the client check just spares a
 * round-trip on obvious typos.
 */

const PIN_RE = /^[0-9]{4,6}$/;

type Mode = "idle" | "set" | "change" | "clear";

export interface PinManagerProps {
  readonly profile: Profile;
}

export function PinManager({ profile }: PinManagerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>("idle");
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMode("idle");
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
    setError(null);
  };

  const setMutation = useMutation({
    mutationFn: (body: { current_pin?: string; new_pin: string }) => getPosClient().setPin(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      reset();
    },
  });

  const clearMutation = useMutation({
    mutationFn: (body: { current_pin: string }) => getPosClient().clearPin(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      reset();
    },
  });

  const pending = setMutation.isPending || clearMutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (mode === "clear") {
      if (!PIN_RE.test(currentPin)) {
        setError(t("profile.pin.errors.invalidFormat"));
        return;
      }
      clearMutation.mutate(
        { current_pin: currentPin },
        {
          onError: (err) => setError(extractApiError(err, t)),
        },
      );
      return;
    }

    // set or change
    if (!PIN_RE.test(newPin)) {
      setError(t("profile.pin.errors.invalidFormat"));
      return;
    }
    if (newPin !== confirmPin) {
      setError(t("profile.pin.errors.confirmMismatch"));
      return;
    }
    if (mode === "change" && !PIN_RE.test(currentPin)) {
      setError(t("profile.pin.errors.invalidFormat"));
      return;
    }

    const body: { current_pin?: string; new_pin: string } =
      mode === "change" ? { current_pin: currentPin, new_pin: newPin } : { new_pin: newPin };
    setMutation.mutate(body, {
      onError: (err) => setError(extractApiError(err, t)),
    });
  }

  return (
    <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t("profile.pin.title")}</h2>
          <p className="mt-1 text-sm text-muted">
            {profile.has_pin ? t("profile.pin.statusSet") : t("profile.pin.statusUnset")}
            {profile.has_pin && profile.pin_set_at && (
              <>
                {" "}
                <span className="text-faint">
                  {t("profile.pin.lastSet", {
                    date: new Date(profile.pin_set_at).toLocaleDateString(),
                  })}
                </span>
              </>
            )}
          </p>
        </div>
        {mode === "idle" && (
          <div className="flex gap-2">
            {profile.has_pin ? (
              <>
                <button
                  type="button"
                  onClick={() => setMode("change")}
                  className="rounded-md border border-line-strong bg-surface px-3 py-1.5 text-xs font-medium text-foreground hover:bg-surface-muted"
                >
                  {t("profile.pin.change")}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("clear")}
                  className="rounded-md border border-line-strong bg-surface px-3 py-1.5 text-xs font-medium text-foreground hover:bg-surface-muted"
                >
                  {t("profile.pin.clear")}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setMode("set")}
                className="rounded-md bg-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary-fg shadow-sm hover:opacity-90"
              >
                {t("profile.pin.set")}
              </button>
            )}
          </div>
        )}
      </header>

      {mode !== "idle" && (
        <form onSubmit={onSubmit} className="mt-4 space-y-3">
          {(mode === "change" || mode === "clear") && (
            <PinField
              label={t("profile.pin.currentLabel")}
              value={currentPin}
              onChange={setCurrentPin}
              autoFocus
            />
          )}
          {mode !== "clear" && (
            <>
              <PinField
                label={mode === "set" ? t("profile.pin.newLabel") : t("profile.pin.newLabelChange")}
                value={newPin}
                onChange={setNewPin}
                autoFocus={mode === "set"}
              />
              <PinField
                label={t("profile.pin.confirmLabel")}
                value={confirmPin}
                onChange={setConfirmPin}
              />
            </>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="submit"
              disabled={pending}
              className="inline-flex items-center gap-2 rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-fg shadow-sm hover:opacity-90 disabled:opacity-60"
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "clear" ? t("profile.pin.clearConfirm") : t("common.saveChanges")}
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={pending}
              className="rounded-md border border-line-strong bg-surface px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-muted disabled:opacity-60"
            >
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

interface PinFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly autoFocus?: boolean;
}

/** Numeric input restricted to 6 digits; ``inputMode="numeric"`` so
 *  mobile keyboards open the digit pad. ``autoComplete="off"`` because
 *  this is a per-user PIN, not a password manager target. */
function PinField({ label, value, onChange, autoFocus }: PinFieldProps) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
        inputMode="numeric"
        autoComplete="off"
        maxLength={6}
        autoFocus={autoFocus}
        className="mt-1 block w-full rounded-md border border-line-strong px-3 py-2 text-sm tracking-[0.4em] shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
      />
    </label>
  );
}

/** Pull a friendly message out of a fetch error. The API returns
 *  ``{ error: "current_pin_invalid" }`` etc.; map known codes to
 *  i18n keys, fall back to the raw message otherwise. */
function extractApiError(err: unknown, t: (key: string) => string): string {
  const code =
    err && typeof err === "object" && "code" in err && typeof err.code === "string"
      ? err.code
      : null;
  const known: Record<string, string> = {
    current_pin_invalid: "profile.pin.errors.currentMismatch",
    invalid_new_pin: "profile.pin.errors.invalidFormat",
  };
  if (code && known[code]) return t(known[code]);
  if (err instanceof Error && err.message) return err.message;
  return t("profile.pin.errors.unknown");
}
